import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Users, CheckCircle, Clock, Search, Lightbulb, Wrench, Search as SearchIcon, Heart, Target, Megaphone, Download, UserPlus, Settings, Tag, HelpCircle, MessageSquare, BarChart3, PieChart, Layers, Send, CreditCard } from 'lucide-react';
import UserDetailModal from '../components/UserDetailModal';
import TeamBuilderModal from '../components/TeamBuilderModal';
import OrganizationSettingsModal from '../components/OrganizationSettingsModal';
import PromoCodeManager from '../components/PromoCodeManager';
import SupportRequestsManager from '../components/SupportRequestsManager';
import TeamCompositionDashboard from '../components/TeamCompositionDashboard';
import OrganizationAnalytics from '../components/OrganizationAnalytics';
import MinistryManager from '../components/MinistryManager';
import CreditTransferModal from '../components/CreditTransferModal';

const AdminDashboard = ({ auth }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [filterDimension, setFilterDimension] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showTeamBuilder, setShowTeamBuilder] = useState(false);
  const [showOrgSettings, setShowOrgSettings] = useState(false);
  const [showPromoCodes, setShowPromoCodes] = useState(false);
  const [showSupportRequests, setShowSupportRequests] = useState(false);
  const [showCreditTransfer, setShowCreditTransfer] = useState(false);
  const [adminCredits, setAdminCredits] = useState(auth.user.credits || 0);
  const [activeTab, setActiveTab] = useState('users');
  
  // Check if user is super admin (no organization_id)
  const isSuperAdmin = !auth.user.organization_id;

  useEffect(() => {
    fetchUsers();
    fetchAdminCredits();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [searchQuery, filterDimension, users]);

  const fetchAdminCredits = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setAdminCredits(response.data.credits || 0);
    } catch (error) {
      console.error('Failed to fetch admin credits');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(profile => 
        profile.user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        profile.user.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Dimension filter
    if (filterDimension !== 'all') {
      filtered = filtered.filter(profile => 
        profile.assessment_result?.primary_dimension === filterDimension
      );
    }

    setFilteredUsers(filtered);
  };

  const exportToCSV = () => {
    // Organized headers: Basic Info | Assessment Results
    const headers = [
      // Basic Info
      'Name', 
      'Email',
      // Assessment Results
      'Assessment Status',
      'Primary Strength',
      'Secondary Strength',
      'Brainstorm Score',
      'Uncover Score',
      'Inspect Score',
      'Lift Score',
      'Deliver Score',
      'Rally Score',
      'Total Score',
      'Assessment Date'
    ];
    
    const rows = filteredUsers.map(profile => {
      const hasAssessment = profile.assessment_result;
      const assessmentDate = hasAssessment 
        ? new Date(profile.assessment_result.completed_at).toLocaleDateString()
        : '';
      
      const totalScore = hasAssessment
        ? Object.values(profile.assessment_result.scores).reduce((sum, score) => sum + score, 0)
        : '';

      return [
        // Basic Info
        profile.user.name,
        profile.user.email,
        // Assessment Results
        hasAssessment ? 'Completed' : 'Not Completed',
        hasAssessment ? dimensionNames[profile.assessment_result.primary_dimension] : 'N/A',
        hasAssessment ? dimensionNames[profile.assessment_result.secondary_dimension] : 'N/A',
        hasAssessment ? profile.assessment_result.scores.B : '',
        hasAssessment ? profile.assessment_result.scores.U : '',
        hasAssessment ? profile.assessment_result.scores.I : '',
        hasAssessment ? profile.assessment_result.scores.L : '',
        hasAssessment ? profile.assessment_result.scores.D : '',
        hasAssessment ? profile.assessment_result.scores.R : '',
        totalScore,
        assessmentDate
      ];
    });

    // Create CSV with proper formatting
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Handle cells that might contain commas
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const filename = auth.user.organization_name 
      ? `${auth.user.organization_name.replace(/[^a-z0-9]/gi, '_')}_volunteers_${new Date().toISOString().split('T')[0]}.csv`
      : `buildr_volunteers_${new Date().toISOString().split('T')[0]}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${filteredUsers.length} volunteers to CSV`);
  };

  const dimensionIcons = {
    B: Lightbulb,
    U: Wrench,
    I: SearchIcon,
    L: Heart,
    D: Target,
    R: Megaphone
  };

  const dimensionColors = {
    B: { bg: 'bg-purple-100', text: 'text-purple-700' },
    U: { bg: 'bg-blue-100', text: 'text-blue-700' },
    I: { bg: 'bg-green-100', text: 'text-green-700' },
    L: { bg: 'bg-pink-100', text: 'text-pink-700' },
    D: { bg: 'bg-orange-100', text: 'text-orange-700' },
    R: { bg: 'bg-red-100', text: 'text-red-700' }
  };

  const dimensionNames = {
    B: 'Brainstorm',
    U: 'Uncover',
    I: 'Inspect',
    L: 'Lift',
    D: 'Deliver',
    R: 'Rally'
  };

  const stats = {
    total: users.length,
    completed: users.filter(u => u.assessment_result).length,
    pending: users.filter(u => !u.assessment_result).length
  };

  const dimensionCounts = {
    B: users.filter(u => u.assessment_result?.primary_dimension === 'B').length,
    U: users.filter(u => u.assessment_result?.primary_dimension === 'U').length,
    I: users.filter(u => u.assessment_result?.primary_dimension === 'I').length,
    L: users.filter(u => u.assessment_result?.primary_dimension === 'L').length,
    D: users.filter(u => u.assessment_result?.primary_dimension === 'D').length,
    R: users.filter(u => u.assessment_result?.primary_dimension === 'R').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            data-testid="back-to-user-dashboard"
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to My Dashboard
          </Button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-5xl font-bold mb-4 text-gray-800" data-testid="admin-dashboard-title">Admin Dashboard</h1>
              {auth.user.organization_name && (
                <Badge className="mb-3 text-base px-4 py-1 bg-blue-100 text-blue-800 border-blue-300">
                  {auth.user.organization_name}
                </Badge>
              )}
              <p className="text-xl text-gray-600">
                {auth.user.organization_name 
                  ? `Manage your organization's volunteers and assessments`
                  : 'Manage volunteers and view assessment results'}
              </p>
              
              {/* Admin Credit Balance */}
              {auth.user.organization_name && (
                <div className="mt-4 flex items-center gap-4">
                  <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-lg border border-indigo-200">
                    <CreditCard className="w-5 h-5 text-indigo-600" />
                    <span className="text-sm text-indigo-700">Your Credits:</span>
                    <span className="text-lg font-bold text-indigo-600" data-testid="admin-credit-display">{adminCredits}</span>
                  </div>
                  <Button
                    data-testid="transfer-credits-btn"
                    onClick={() => setShowCreditTransfer(true)}
                    variant="outline"
                    className="border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                    disabled={adminCredits === 0}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Transfer Credits
                  </Button>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              {isSuperAdmin && (
                <>
                  <Button
                    data-testid="promo-codes-btn"
                    onClick={() => { setShowPromoCodes(!showPromoCodes); setShowSupportRequests(false); }}
                    variant={showPromoCodes ? "default" : "outline"}
                    className={showPromoCodes ? "bg-indigo-600 hover:bg-indigo-700" : "border-indigo-600 text-indigo-600 hover:bg-indigo-50"}
                  >
                    <Tag className="mr-2 h-4 w-4" />
                    Promo Codes
                  </Button>
                  <Button
                    data-testid="support-requests-btn"
                    onClick={() => { setShowSupportRequests(!showSupportRequests); setShowPromoCodes(false); }}
                    variant={showSupportRequests ? "default" : "outline"}
                    className={showSupportRequests ? "bg-amber-600 hover:bg-amber-700" : "border-amber-600 text-amber-600 hover:bg-amber-50"}
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Support
                  </Button>
                </>
              )}
              {auth.user.organization_name && (
                <Button
                  data-testid="org-settings-btn"
                  onClick={() => setShowOrgSettings(true)}
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Organization Settings
                </Button>
              )}
              <Button
                data-testid="team-builder-btn"
                onClick={() => setShowTeamBuilder(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Team Builder
              </Button>
              <Button
                data-testid="export-csv-btn"
                onClick={exportToCSV}
                className="bg-green-600 hover:bg-green-700"
              >
                <Download className="mr-2 h-4 w-4" />
                Export to CSV
              </Button>
              <Button
                data-testid="admin-help-btn"
                onClick={() => navigate('/help')}
                variant="outline"
              >
                <HelpCircle className="mr-2 h-4 w-4" />
                Help
              </Button>
            </div>
          </div>
        </div>

        {/* Promo Codes Section (Super Admin Only) */}
        {isSuperAdmin && showPromoCodes && (
          <div className="mb-8">
            <PromoCodeManager />
          </div>
        )}

        {/* Support Requests Section (Super Admin Only) */}
        {isSuperAdmin && showSupportRequests && (
          <div className="mb-8">
            <SupportRequestsManager />
          </div>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="composition" className="flex items-center gap-2">
              <PieChart className="h-4 w-4" />
              <span className="hidden sm:inline">Team Composition</span>
            </TabsTrigger>
            <TabsTrigger value="ministries" className="flex items-center gap-2">
              <Layers className="h-4 w-4" />
              <span className="hidden sm:inline">Ministries</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Analytics</span>
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            {/* Stats Overview */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="shadow-lg" data-testid="total-users-stat">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-6 w-6 text-indigo-600" />
                    Total Users
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-indigo-600">{stats.total}</div>
                </CardContent>
              </Card>

              <Card className="shadow-lg" data-testid="completed-stat">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    Completed Assessments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-green-600">{stats.completed}</div>
                </CardContent>
              </Card>

              <Card className="shadow-lg" data-testid="pending-stat">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-6 w-6 text-orange-600" />
                    Pending Assessments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-orange-600">{stats.pending}</div>
                </CardContent>
              </Card>
            </div>

            {/* Dimension Distribution */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>BUILDR Dimension Distribution</CardTitle>
                <CardDescription>Primary strengths across all volunteers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {Object.entries(dimensionCounts).map(([dimension, count]) => {
                    const Icon = dimensionIcons[dimension];
                    const color = dimensionColors[dimension];
                    return (
                      <button
                        key={dimension}
                        onClick={() => setFilterDimension(filterDimension === dimension ? 'all' : dimension)}
                        className={`p-4 ${color.bg} rounded-lg text-center hover:opacity-80 transition-opacity ${
                          filterDimension === dimension ? 'ring-2 ring-offset-2 ring-indigo-600' : ''
                        }`}
                        data-testid={`dimension-filter-${dimension}`}
                      >
                        <Icon className={`w-8 h-8 ${color.text} mx-auto mb-2`} />
                        <div className="text-2xl font-bold">{count}</div>
                        <div className="text-xs">{dimensionNames[dimension]}</div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Search and Filters */}
            <Card>
              <CardHeader>
                <CardTitle>Search Volunteers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  data-testid="search-users-input"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              {filterDimension !== 'all' && (
                <Button
                  data-testid="clear-filter-btn"
                  onClick={() => setFilterDimension('all')}
                  variant="outline"
                >
                  Clear Filter
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>All Volunteers ({filteredUsers.length})</CardTitle>
            <CardDescription>View and manage volunteer profiles</CardDescription>
          </CardHeader>
          <CardContent>
            {filteredUsers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No volunteers found matching your criteria
              </div>
            ) : (
              <div className="space-y-4">
                {filteredUsers.map((profile) => (
                  <div
                    key={profile.user.id}
                    onClick={() => setSelectedUser(profile)}
                    className="p-6 border-2 rounded-lg hover:border-indigo-300 transition-colors cursor-pointer"
                    data-testid={`user-card-${profile.user.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold">{profile.user.name}</h3>
                          <Badge variant="outline">{profile.user.role}</Badge>
                        </div>
                        <p className="text-gray-600 mb-4">{profile.user.email}</p>
                        
                        {profile.assessment_result ? (
                          <div className="flex flex-wrap gap-3">
                            <div className={`px-4 py-2 ${dimensionColors[profile.assessment_result.primary_dimension].bg} rounded-lg`}>
                              <div className="flex items-center gap-2">
                                {(() => {
                                  const Icon = dimensionIcons[profile.assessment_result.primary_dimension];
                                  return <Icon className={`w-5 h-5 ${dimensionColors[profile.assessment_result.primary_dimension].text}`} />;
                                })()}
                                <div>
                                  <div className="text-xs text-gray-600">Primary</div>
                                  <div className="font-semibold">{dimensionNames[profile.assessment_result.primary_dimension]}</div>
                                </div>
                              </div>
                            </div>
                            <div className={`px-4 py-2 ${dimensionColors[profile.assessment_result.secondary_dimension].bg} rounded-lg opacity-75`}>
                              <div className="flex items-center gap-2">
                                {(() => {
                                  const Icon = dimensionIcons[profile.assessment_result.secondary_dimension];
                                  return <Icon className={`w-5 h-5 ${dimensionColors[profile.assessment_result.secondary_dimension].text}`} />;
                                })()}
                                <div>
                                  <div className="text-xs text-gray-600">Secondary</div>
                                  <div className="font-semibold">{dimensionNames[profile.assessment_result.secondary_dimension]}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-orange-600 border-orange-300">
                            Assessment Pending
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Team Composition Tab */}
          <TabsContent value="composition">
            <TeamCompositionDashboard />
          </TabsContent>

          {/* Ministries Tab */}
          <TabsContent value="ministries">
            <MinistryManager allUsers={users} />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <OrganizationAnalytics />
          </TabsContent>
        </Tabs>

        {/* User Detail Modal */}
        {selectedUser && (
          <UserDetailModal
            user={selectedUser}
            onClose={() => setSelectedUser(null)}
            onUpdate={() => {
              fetchUsers();
              setSelectedUser(null);
            }}
            isSuperAdmin={isSuperAdmin}
          />
        )}

        {/* Team Builder Modal */}
        {showTeamBuilder && (
          <TeamBuilderModal
            users={users}
            onClose={() => setShowTeamBuilder(false)}
          />
        )}

        {/* Organization Settings Modal */}
        {showOrgSettings && (
          <OrganizationSettingsModal
            onClose={() => setShowOrgSettings(false)}
            onUpdate={() => fetchUsers()}
          />
        )}

        {/* Credit Transfer Modal */}
        {showCreditTransfer && (
          <CreditTransferModal
            onClose={() => setShowCreditTransfer(false)}
            onTransferComplete={(result) => {
              setAdminCredits(result.admin_credits_remaining);
              fetchUsers(); // Refresh user list to show updated credits
            }}
            users={users}
            adminCredits={adminCredits}
          />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
